import React from 'react';
import {
    Modal,
    Radio,
    InputNumber,
    Input,
    DatePicker,
    Select,
    Button,
    Typography,
    Form
} from 'antd';
import type { RadioChangeEvent } from 'antd';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Text } = Typography;

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: {
        date: string;
        type: 'late' | 'early';
        minutes: number;
        reason: string;
        notify: string[];
    }) => void;
    date: string;
};

export const PartialDayRequestModal: React.FC<Props> = ({ open, onClose, onSubmit, date }) => {
    const [form] = Form.useForm();
    const type = Form.useWatch('type', form) || 'late';

    const handleFinish = (values: any) => {
        onSubmit({
            date: dayjs(date).format('YYYY-MM-DD'),
            type: values.type,
            minutes: values.minutes,
            reason: values.reason,
            notify: values.notify || [],
        });
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={<div className="flex justify-end gap-2">
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button type="primary" onClick={() => form.submit()}>
                    Confirm
                </Button>
            </div>}
            centered
            title={<span className="text-lg font-semibold">Partial Day Request</span>}
            className="!top-10"
            bodyStyle={{ backgroundColor: '#fff' }}
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{
                    type: 'late',
                    minutes: 1,
                    notify: []
                }}
                className="p-4 flex flex-col gap-6"
            >
                <Form.Item
                    name="date"
                    label={<Text className="text-gray-700 font-medium">Select Date</Text>}
                    rules={[{ required: true, message: 'Please select a date' }]}
                >
                    <DatePicker format="DD/MM/YYYY" className="w-full" />
                </Form.Item>

                <Form.Item name="type">
                    <Radio.Group className="flex gap-8">
                        <Radio value="late">Late Arrival</Radio>
                        <Radio value="early">Leaving Early</Radio>
                    </Radio.Group>
                </Form.Item>

                <Form.Item
                    name="minutes"
                    label={
                        <span className="text-gray-700">
                            {type === 'late' ? 'Will come late by' : 'Will leave early by'}
                        </span>
                    }
                    rules={[{ required: true, message: 'Please enter number of minutes' }]}
                >
                    <InputNumber min={1} max={240} addonAfter="minutes" className="w-full" />
                </Form.Item>

                <Form.Item
                    name="reason"
                    label={<Text className="text-gray-700 font-medium">Reason</Text>}
                    rules={[{ required: true, message: 'Please provide a reason' }]}
                >
                    <TextArea rows={3} />
                </Form.Item>

                <Form.Item
                    name="notify"
                    label={<Text className="text-gray-700 font-medium">Notify</Text>}
                >
                    <Select
                        mode="multiple"
                        placeholder="Search Employee"
                        options={[
                            { value: 'john.doe', label: 'John Doe' },
                            { value: 'jane.smith', label: 'Jane Smith' },
                            // Extend with dynamic employee list if needed
                        ]}
                    />
                </Form.Item>
                <Text className="text-xs text-gray-500 -mt-4">
                    These employees will be notified through mail when your partial day request is approved.
                </Text>


            </Form>
        </Modal>
    );
};
